import { Asset } from "expo-asset";
import { FC, useState } from "react";

import balanced from "@/assets/images/launch-simulator/jar-balanced.png";
import nutellaLed from "@/assets/images/launch-simulator/jar-nutella-led.png";
import nutellaOnly from "@/assets/images/launch-simulator/jar-nutella-only.png";
import peanutLed from "@/assets/images/launch-simulator/jar-peanut-led.png";
import peanutOnly from "@/assets/images/launch-simulator/jar-peanut-only.png";
import { Box, Media, MediaGallery, Text, VStack } from "@/components/ui";

import { FACTORS } from "./model";
import { Configuration } from "./types";

/* ── Types ─────────────────────────────────────── */

interface PackPreviewProps {
  configuration: Configuration;
  /** Whether clicking the jar opens the gallery of all designs. */
  gallery?: boolean;
  /** Rendered height in px; width follows the jar's aspect ratio. */
  height?: number;
  /** Override the pack design (used by the hover previews). */
  packaging?: string;
}

/* ── Constants ─────────────────────────────────── */

const ASPECT = 0.75;
/** Height reserved under the jar for the size/price and claim lines. */
const CAPTION_H = 40;

/** Packshot per design level, in scale order (Nutella-only → peanut-only). */
const PACK_IMAGES: { uri: string; value: string }[] = [
  { uri: Asset.fromModule(nutellaOnly).uri, value: "nutellaOnly" },
  { uri: Asset.fromModule(nutellaLed).uri, value: "nutellaLed" },
  { uri: Asset.fromModule(balanced).uri, value: "balanced" },
  { uri: Asset.fromModule(peanutLed).uri, value: "peanutLed" },
  { uri: Asset.fromModule(peanutOnly).uri, value: "peanutOnly" },
];

const SIZE_SCALE: Record<string, number> = { "12": 0.86, "16": 1, "20": 1.12 };

/* ── Helpers ───────────────────────────────────── */

/** Packshot URI for a pack design value (falls back to the co-branded jar). */
export const packImageUri = (packaging: string): string =>
  (PACK_IMAGES.find((p) => p.value === packaging) ?? PACK_IMAGES[2]).uri;

/* ── Component ─────────────────────────────────── */

/** Product visual that follows the current configuration. */
const PackPreview: FC<PackPreviewProps> = ({
  configuration,
  gallery = false,
  height = 200,
  packaging,
}) => {
  const [open, setOpen] = useState(false);
  const design = packaging ?? configuration.packaging;
  const index = Math.max(
    0,
    PACK_IMAGES.findIndex((p) => p.value === design)
  );
  // The container is sized for the largest jar so switching sizes never
  // shifts the layout below.
  const scale = SIZE_SCALE[configuration.packSize] ?? 1;
  const maxScale = Math.max(...Object.values(SIZE_SCALE));
  const slotH = height - CAPTION_H;
  const jarH = Math.round((slotH / maxScale) * scale);
  const claim =
    configuration.claim === "none"
      ? undefined
      : optionLabel("claim", configuration.claim);
  const price = optionLabel("price", configuration.price);

  return (
    <VStack className="items-center" style={{ height }}>
      <Box className="justify-end" style={{ height: slotH }}>
        <Media
          alt={`Pack design: ${optionLabel("packaging", design)}`}
          className="border-0 border-transparent"
          height={jarH}
          // Dev asset URIs differ only by query string; force a remount per design.
          key={PACK_IMAGES[index].uri}
          onPress={gallery ? () => setOpen(true) : undefined}
          resizeMode="contain"
          source={PACK_IMAGES[index].uri}
          width={Math.round(jarH * ASPECT)}
        />
      </Box>
      <VStack className="items-center" style={{ height: CAPTION_H }}>
        <Text className="text-xs text-typography-500">
          {configuration.packSize} oz{price ? ` · ${price}` : ""}
        </Text>
        <Text className="text-[11px] italic text-typography-400">
          {claim ? `“${claim}”` : " "}
        </Text>
      </VStack>
      {gallery && (
        <MediaGallery
          media={[PACK_IMAGES[index].uri]}
          onClose={() => setOpen(false)}
          visible={open}
        />
      )}
    </VStack>
  );
};

/* ── Helpers ───────────────────────────────────── */

const optionLabel = (factor: string, value: string): string | undefined =>
  FACTORS.find((f) => f.id === factor)?.options.find((o) => o.value === value)
    ?.label;

export default PackPreview;
